import { Search as SearchIcon } from "@mui/icons-material";
import { Box, InputAdornment, Paper, Popper, TextField } from "@mui/material";
import React, { useEffect, useId, useMemo, useRef, useState } from "react";

import { monospaceStyle } from "../../theme";
import { SuggestionGroup } from "../../types/productPlane";
import ExploreSuggestions, {
  FlatSuggestion,
  flattenSuggestions,
  sideSuggestion,
  suggestionOptionId,
} from "./ExploreSuggestions";

// Pause (ms) in the typing after which the text is asked for suggestions
export const SUGGEST_DELAY_MS = 200;

const NO_SUGGESTIONS: readonly SuggestionGroup[] = [];

interface ExploreSearchFieldProps {
  // The submitted keyword
  keyword: string;
  // Called with the trimmed text when the field is submitted
  onSubmit: (keyword: string) => void;
  // What is searched, for the field's name, e.g. "products"
  subject: string;
  // The hint the empty field shows: what the keyword is matched against
  placeholder: string;
  // Called with the text once the typing pauses, so that the owner can ask
  // for its suggestions; with "" when the text is the keyword again. The
  // field offers suggestions only when this is given.
  onAsk?: (text: string) => void;
  // The suggestions for the text last asked
  suggestions?: readonly SuggestionGroup[];
  // Called with a suggestion the user picks; after a pick the field shows
  // the keyword again
  onPickSuggestion?: (picked: FlatSuggestion) => void;
}

// Whether the browser runs on an Apple platform, where the shortcut is
// written with the command key
const isApplePlatform = (): boolean =>
  /Mac|iPhone|iPad|iPod/.test(navigator.platform);

// Whether a key press is the shortcut that focuses the field: command or
// control with K, and no other modifier
const isFocusShortcut = (event: KeyboardEvent): boolean =>
  (event.metaKey || event.ctrlKey) &&
  !event.altKey &&
  !event.shiftKey &&
  event.key.toLowerCase() === "k";

// The search field of Explore. Typing changes nothing; submitting sets the
// keyword. Command-K (control-K off Apple platforms) focuses it from
// anywhere on the page. Given `onAsk`, the text typed is handed to the
// owner once the typing pauses, and the suggestions the owner passes back
// are listed under the field, a column per group: up and down choose one,
// left and right move across the columns once one is chosen, Enter or a click picks
// it, Escape closes the list, and Enter with none chosen submits the text
// as before.
const ExploreSearchField: React.FC<ExploreSearchFieldProps> = ({
  keyword,
  onSubmit,
  subject,
  placeholder,
  onAsk,
  suggestions = NO_SUGGESTIONS,
  onPickSuggestion,
}) => {
  const [draft, setDraft] = useState(keyword);
  const inputRef = useRef<HTMLInputElement>(null);
  const anchorRef = useRef<HTMLDivElement>(null);
  const listId = useId();
  const suggesting = Boolean(onAsk);
  const ask = useRef(onAsk);
  ask.current = onAsk;

  // Whether the list may show: the field has the focus, and the list was
  // not closed since the last change of the text
  const [listOpen, setListOpen] = useState(false);
  // The option the arrows are on, by its position in the list
  const [active, setActive] = useState<number | null>(null);

  // The keyword changes without the field when the address does: the back
  // button, a cleared keyword, an opened link
  useEffect(() => {
    setDraft(keyword);
  }, [keyword]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (isFocusShortcut(event) && inputRef.current) {
        event.preventDefault();
        inputRef.current.focus();
        inputRef.current.select();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  // The text is handed over once the typing pauses; the submitted keyword
  // itself is not asked for suggestions
  useEffect(() => {
    if (!suggesting) {
      return undefined;
    }
    const text = draft.trim() === keyword.trim() ? "" : draft.trim();
    const timer = window.setTimeout(
      () => ask.current?.(text),
      SUGGEST_DELAY_MS,
    );
    return () => window.clearTimeout(timer);
  }, [draft, keyword, suggesting]);

  const options = useMemo(() => flattenSuggestions(suggestions), [suggestions]);
  const showList = suggesting && listOpen && options.length > 0;

  // A new list starts with no option chosen
  useEffect(() => {
    setActive(null);
  }, [suggestions]);

  const pick = (picked: FlatSuggestion) => {
    setListOpen(false);
    setActive(null);
    setDraft(keyword);
    ask.current?.("");
    onPickSuggestion?.(picked);
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (!showList) {
      return;
    }
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      const step = event.key === "ArrowDown" ? 1 : -1;
      setActive((current) => {
        if (current === null) {
          return step > 0 ? 0 : options.length - 1;
        }
        const next = current + step;
        return next < 0 || next >= options.length ? null : next;
      });
    } else if (
      (event.key === "ArrowLeft" || event.key === "ArrowRight") &&
      active !== null
    ) {
      // Across the columns, once an option is chosen; before that the
      // arrows move the caret in the text
      event.preventDefault();
      const side = sideSuggestion(
        suggestions,
        active,
        event.key === "ArrowRight" ? 1 : -1,
      );
      if (side !== null) {
        setActive(side);
      }
    } else if (event.key === "Enter" && active !== null) {
      event.preventDefault();
      pick(options[active]);
    } else if (event.key === "Escape") {
      event.preventDefault();
      setListOpen(false);
      setActive(null);
    }
  };

  const submit = () => {
    setListOpen(false);
    onSubmit(draft.trim());
  };

  const apple = isApplePlatform();

  return (
    <form
      role="search"
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
    >
      <TextField
        fullWidth
        size="small"
        ref={anchorRef}
        value={draft}
        onChange={(event) => {
          setDraft(event.target.value);
          setListOpen(true);
        }}
        onKeyDown={onKeyDown}
        onFocus={() => setListOpen(true)}
        onBlur={() => setListOpen(false)}
        placeholder={placeholder}
        inputRef={inputRef}
        inputProps={{
          "aria-label": `Search ${subject}`,
          "aria-keyshortcuts": apple ? "Meta+K" : "Control+K",
          maxLength: 200,
          ...(suggesting
            ? {
                role: "combobox",
                "aria-autocomplete": "list",
                "aria-expanded": showList,
                "aria-controls": showList ? listId : undefined,
                "aria-activedescendant":
                  showList && active !== null
                    ? suggestionOptionId(listId, active)
                    : undefined,
              }
            : {}),
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" sx={{ color: "text.secondary" }} />
            </InputAdornment>
          ),
          // The shortcut, as a hint; the field names it to assistive
          // technology itself
          endAdornment: (
            <InputAdornment position="end" aria-hidden>
              <Box
                component="kbd"
                sx={{
                  ...monospaceStyle,
                  fontSize: 11,
                  lineHeight: 1.5,
                  color: "text.secondary",
                  border: "1px solid rgba(255, 255, 255, 0.12)",
                  borderRadius: "4px",
                  px: "6px",
                  py: "1px",
                }}
              >
                {apple ? "⌘K" : "Ctrl K"}
              </Box>
            </InputAdornment>
          ),
        }}
        sx={{
          "& .MuiOutlinedInput-root": { backgroundColor: "background.paper" },
        }}
      />
      <Popper
        open={showList}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        sx={{ zIndex: (theme) => theme.zIndex.modal }}
        style={{ width: anchorRef.current?.clientWidth }}
      >
        <Paper elevation={8} sx={{ mt: 0.5 }}>
          <ExploreSuggestions
            id={listId}
            groups={suggestions}
            active={active}
            onPick={pick}
            text={draft}
            onSearch={submit}
          />
        </Paper>
      </Popper>
    </form>
  );
};

export default ExploreSearchField;
